import { Accordion, AccordionDetails, AccordionSummary, Grid, MenuItem, Typography } from "@mui/material"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import { enqueueSnackbar } from "notistack"
import { makeStyles } from "tss-react/mui"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faArrowUpRightFromSquare } from "@fortawesome/pro-light-svg-icons"

import { useCallback, useMemo, useState } from "react"
import { MRT_ColumnDef } from "material-react-table"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import {
    FetchBankAccountsResponse,
    deleteBankAccountItem as deleteBankAccount,
    fetchBankAccountsResult,
    POSQuery,
} from "../SupplierAdminPOSApi"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { ConfirmModal } from "../../../../../../shared/components/dialogs/ConfirmModal"
import { useApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import { BankAccount } from "../SupplierAdminPOSTypes"
import { AcquirersStatusChip } from "./AcquirersStatusChip"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import { useCurrentSupplier } from "../../../../../suppliers/supplierHooks"
import { ExternalLink } from "../../../../../../shared/components/links/ExternalLink"
import { useHostedOnboarding } from "../acquiringHooks"

const useStyles = makeStyles()((theme) => ({
    accordion: {
        borderRadius: theme.spacing(1.5),
        // Must override this too
        "&:last-of-type": {
            borderBottomLeftRadius: theme.spacing(1.5),
            borderBottomRightRadius: theme.spacing(1.5),
        },
    },
    accordionSummary: {
        paddingLeft: "2.5rem",
        paddingRight: "2.5rem",
    },
    accordionDetails: {
        paddingLeft: "2.5rem",
        paddingRight: "2.5rem",
        paddingBottom: "20px", // NOTE: Matches default top spacing in summary
    },
    tableContainer: {
        maxHeight: "100%",
    },
    tableHeadCell: {
        border: "none",
        padding: "0.75rem 1.5rem",
        color: theme.palette.tailwind.slate[500],
    },
    tableHeadRow: {
        boxShadow: "none",
        background: theme.palette.tailwind.slate[50],
    },
    topToolbar: {
        display: "none",
    },
    actionWrap: {
        display: "flex",
        flexWrap: "wrap",
        gap: "0.8rem",
        padding: "0.8rem",
    },
    linkText: {
        color: "#2196F3",
        cursor: "pointer",
    },
}))

type ConfirmModalState = Readonly<{
    bankAccountId: string
    bankAccountIdentifier: string
}>

function selectBankAccounts(data: FetchBankAccountsResponse) {
    return data.bankAccounts
}

type Props = Readonly<{
    agreementId: number
}>

export function AcquirerBankAccounts({ agreementId }: Props) {
    const { l10n } = useAppLocalization()
    const { classes } = useStyles()
    const { supplierId } = useCurrentSupplier()
    const { handleHostedOnboarding } = useHostedOnboarding(agreementId)

    const [confirmModalState, setConfirmModalState] = useState<ConfirmModalState>()

    const bankAccountsResult = useApiQuery(fetchBankAccountsResult, {
        queryName: POSQuery.BANK_ACCOUNT_CODES_DETAILS,
        dependencies: {
            supplierId,
        },
        select: selectBankAccounts,
    })

    const { mutate: callDeleteBankAccount } = useApiMutation(deleteBankAccount, {
        async onSuccess(_params, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [POSQuery.BANK_ACCOUNT_CODES_DETAILS] })

            enqueueSnackbar({
                variant: "success",
                message: data.serviceResponse.displayMessage,
            })
        },
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(bankAccountsResult)

    const columnsBankAccounts = useMemo<MRT_ColumnDef<BankAccount>[]>(
        () => [
            columnFactory.shortNameColumn({
                header: l10n.getString("supplier-acquiring-bank-accounts-table-bank-name-column"),
                accessorKey: "bankName",
            }),
            columnFactory.shortNameColumn({
                header: l10n.getString("supplier-acquiring-bank-accounts-table-account-column"),
                accessorKey: "accountIdentifier",
            }),
            columnFactory.shortNameColumn({
                header: l10n.getString("supplier-acquiring-bank-accounts-table-merchant-caseno-column"),
                accessorKey: "merchant.regNumber",
            }),
            columnFactory.shortNameColumn({
                header: l10n.getString("supplier-acquiring-bank-accounts-table-merchant-column"),
                accessorKey: "merchant.name",
            }),
            columnFactory.typeColumn({
                header: l10n.getString("supplier-acquiring-bank-accounts-table-status-column"),
                accessorKey: "status",
                Cell: ({ row }) => <AcquirersStatusChip status={row.original.status} placement="table" />,
            }),
        ],
        [l10n, columnFactory],
    )

    const handleDeleteClick = useCallback(() => {
        if (confirmModalState?.bankAccountId) {
            callDeleteBankAccount({
                supplierId,
                bankAccountId: confirmModalState.bankAccountId,
            })
        }
    }, [confirmModalState?.bankAccountId, callDeleteBankAccount])

    return (
        <>
            <Accordion defaultExpanded className={classes.accordion}>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel2-content"
                    id="panel2-header"
                    className={classes.accordionSummary}
                >
                    <LocalizedStrict id="supplier-acquiring-bank-accounts-accordion-bank-accounts-title">
                        <Typography variant="body1" fontWeight={600}>
                            Bank accounts
                        </Typography>
                    </LocalizedStrict>
                </AccordionSummary>
                <AccordionDetails className={classes.accordionDetails}>
                    <Grid container direction="column">
                        <ApiQueryTable
                            columns={columnsBankAccounts}
                            enableClickToCopy={false}
                            enableColumnOrdering
                            enableStickyHeader
                            enableDensityToggle={false}
                            initialState={{ density: "spacious" }}
                            enableHiding={false}
                            enableRowActions={false} // TODO: Re-enable once API supports deleting bank accounts
                            enableColumnActions={false}
                            enableGrouping
                            muiTableContainerProps={{ className: classes.tableContainer }}
                            muiTablePaperProps={{ elevation: 0 }}
                            muiTableHeadCellProps={{ className: classes.tableHeadCell }}
                            muiTableHeadRowProps={{ className: classes.tableHeadRow }}
                            muiTopToolbarProps={{ className: classes.topToolbar }}
                            positionActionsColumn="last"
                            enableColumnDragging={false}
                            renderRowActionMenuItems={({ row, closeMenu }) => [
                                <MenuItem
                                    onClick={() => {
                                        setConfirmModalState({
                                            bankAccountId: row.original.id,
                                            bankAccountIdentifier: `${row.original.accountIdentifier}`,
                                        })
                                        closeMenu()
                                    }}
                                    key={`delete-${row.original.id}`}
                                >
                                    <LocalizedStrict id="supplier-acquiring-bank-accounts-table-remove-action">
                                        <span>Remove</span>
                                    </LocalizedStrict>
                                </MenuItem>,
                            ]}
                            enableToolbarInternalActions={false}
                        />
                    </Grid>
                    <Grid className={classes.actionWrap}>
                        <LocalizedStrict id="supplier-acquiring-bank-accounts-add-bank-account">
                            <StandardButton
                                size="medium"
                                fullWidth={false}
                                color="blue"
                                onClick={handleHostedOnboarding}
                                endIcon={<FontAwesomeIcon icon={faArrowUpRightFromSquare} width="0.75rem" />}
                            >
                                Add new bank account
                            </StandardButton>
                        </LocalizedStrict>
                        <LocalizedStrict
                            id="supplier-acquiring-bank-accounts-info-text"
                            elems={{
                                guideLink: (
                                    <ExternalLink
                                        href="https://example.com"
                                        className={classes.linkText}
                                    ></ExternalLink>
                                ),
                            }}
                        >
                            <Typography variant="caption">
                                If you want sales to be transfered to a separate bank account for each merchant you can
                                add the bank account here. See{" "}
                                <ExternalLink href="https://example.com" className={classes.linkText}>
                                    guide
                                </ExternalLink>{" "}
                                on how to add a new bank account on the Adyen platform and afterwards connect the bank
                                account to your merchant.
                            </Typography>
                        </LocalizedStrict>
                    </Grid>
                </AccordionDetails>
            </Accordion>
            {confirmModalState && (
                <LocalizedStrict
                    id="supplier-acquiring-bank-accounts-confirm-delete-dialog"
                    attrs={{ title: true, message: true, buttonLabelSubmit: true }}
                >
                    <ConfirmModal
                        title="Remove bank account"
                        message={`Do you wish to remove the bank account ${confirmModalState.bankAccountIdentifier}?`}
                        buttonLabelSubmit="Remove"
                        handleClick={handleDeleteClick}
                        open={confirmModalState !== undefined}
                        onClose={() => setConfirmModalState(undefined)}
                    />
                </LocalizedStrict>
            )}
        </>
    )
}
